'use client';

import { SITE_DOMAIN } from '@/config/env';
import { getEnvironment } from '@/lib/environment';
import {
  captureGTMAttribution,
  getFirstTouchPartnerAttribution,
} from '@/lib/gtm';
import type { AlertPayload } from '@/lib/alertTypes';

const ALERT_ENDPOINT = '/internal-api/alert';
const REPORTED_STORAGE_PREFIX = 'musein:partner_attribution_reported:v1';
const inFlightReports = new Set<string>();

function getReportKey(userId: string, partner: string): string {
  return `${REPORTED_STORAGE_PREFIX}:${userId}:${partner}`;
}

function wasReported(key: string): boolean {
  try {
    return window.localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function markReported(key: string): void {
  try {
    window.localStorage.setItem(key, new Date().toISOString());
  } catch {
    // Storage can be unavailable in private browsing. The server-side dedupe window
    // still prevents immediate duplicate messages within the same deployment instance.
  }
}

export async function reportPartnerAttribution(userId: string): Promise<void> {
  if (typeof window === 'undefined' || !userId.trim()) {
    return;
  }

  captureGTMAttribution();
  const attribution = getFirstTouchPartnerAttribution();
  if (!attribution) {
    return;
  }

  const reportKey = getReportKey(userId, attribution.partner);
  if (wasReported(reportKey) || inFlightReports.has(reportKey)) {
    return;
  }

  inFlightReports.add(reportKey);
  try {
    const payload: AlertPayload = {
      action: 'partner_attribution_recovery',
      message: 'Authenticated user partner attribution record',
      siteDomain: SITE_DOMAIN,
      environment: getEnvironment() || 'unknown',
      timestamp: new Date().toISOString(),
      pagePath: window.location.pathname,
      user: { userId },
      partnerAttribution: attribution,
    };

    const response = await fetch(ALERT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    const result = await response.json().catch(() => null) as {
      ok?: boolean;
      throttled?: boolean;
    } | null;

    if (response.ok && result?.ok === true && result.throttled !== true) {
      markReported(reportKey);
    }
  } catch {
    // Attribution reporting must never interrupt authentication or navigation.
  } finally {
    inFlightReports.delete(reportKey);
  }
}
